import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useUser, SignOutButton } from "@clerk/react";
import { Store, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useApi, errMsg } from "@/lib/api";
import { BUSINESS_TYPES } from "@/lib/dates";
import { useMember } from "@/context/MemberContext";
import { Logo, Loader } from "@/components/common";

export default function Onboarding() {
  const api = useApi();
  const nav = useNavigate();
  const { user } = useUser();
  const m = useMember();
  const [fullName, setFullName] = useState(user?.fullName || "");
  const [biz, setBiz] = useState({ name: "", business_type: "restaurant" });
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  if (m.loading) return <Loader full />;
  if (m.member) return <Navigate to="/" replace />;

  const submit = async (path, body) => {
    setBusy(true);
    try {
      await api.post(path, { ...body, full_name: fullName });
      await m.reload();
      toast.success("Welkom bij ShiftMate NL!");
      nav("/", { replace: true });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] px-4 py-10" data-testid="onboarding-page">
      <div className="mx-auto max-w-lg">
        <Logo />
        <h1 className="mt-8 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Welkom! Laten we beginnen.</h1>
        <p className="text-slate-600 mt-2">Start een nieuw bedrijf of sluit je aan bij je team met een uitnodigingscode.</p>
        {m.deactivated && (
          <p className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900" data-testid="deactivated-notice">
            Je account is gedeactiveerd door je werkgever. Neem contact op met je manager.
          </p>
        )}

        {!m.deactivated && (
        <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-5 sm:p-6">
          <Label htmlFor="full-name">Je volledige naam</Label>
          <Input id="full-name" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Bijv. Sanne de Jong" className="mt-1.5 h-11" data-testid="onboarding-name-input" />

          <Tabs defaultValue="new" className="mt-6">
            <TabsList className="grid grid-cols-2 w-full h-11">
              <TabsTrigger value="new" data-testid="onboarding-tab-new"><Store className="h-4 w-4 mr-2" />Nieuw bedrijf</TabsTrigger>
              <TabsTrigger value="join" data-testid="onboarding-tab-join"><KeyRound className="h-4 w-4 mr-2" />Ik heb een code</TabsTrigger>
            </TabsList>
            <TabsContent value="new" className="space-y-4 pt-4">
              <div>
                <Label htmlFor="biz-name">Naam van je zaak</Label>
                <Input id="biz-name" value={biz.name} onChange={(e) => setBiz({ ...biz, name: e.target.value })} placeholder="Bijv. Tokyo Sushi Bar" className="mt-1.5 h-11" data-testid="onboarding-business-name-input" />
              </div>
              <div>
                <Label>Soort zaak</Label>
                <Select value={biz.business_type} onValueChange={(v) => setBiz({ ...biz, business_type: v })}>
                  <SelectTrigger className="mt-1.5 h-11" data-testid="onboarding-business-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(BUSINESS_TYPES).map(([k, v]) => (
                      <SelectItem key={k} value={k} data-testid={`business-type-${k}`}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button className="w-full h-12 rounded-xl" disabled={busy} onClick={() => submit("/onboarding/business", biz)} data-testid="onboarding-create-business-button">
                Bedrijf aanmaken
              </Button>
              <p className="text-xs text-slate-500">Je wordt automatisch de eigenaar van dit bedrijf.</p>
            </TabsContent>
            <TabsContent value="join" className="space-y-4 pt-4">
              <div>
                <Label htmlFor="invite-code">Uitnodigingscode</Label>
                <Input id="invite-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Bijv. K7MP2Q" className="mt-1.5 h-11 font-mono-num tracking-[0.3em] uppercase" data-testid="onboarding-invite-code-input" />
              </div>
              <Button className="w-full h-12 rounded-xl" disabled={busy} onClick={() => submit("/onboarding/join", { invite_code: code })} data-testid="onboarding-join-button">
                Aansluiten bij team
              </Button>
              <p className="text-xs text-slate-500">Je krijgt de code van je eigenaar of manager.</p>
            </TabsContent>
          </Tabs>
        </div>
        )}
        <div className="mt-4 text-center">
          <SignOutButton><Button variant="ghost" size="sm" data-testid="onboarding-signout-button">Uitloggen</Button></SignOutButton>
        </div>
      </div>
    </div>
  );
}
